import TaskItem from '@tiptap/extension-task-item';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';

/**
 * 빈 체크박스 왕복 교정 (`AxRichTextEditor` 전용).
 * 설계: `docs/designs/rich-text-editing-quality-and-block-features.md` §5 F4
 *
 * 내용이 비어 있는 체크 항목은 `- [ ] `(끝에 공백)으로 직렬화되는데, `markdown-it` 이
 * 블록 파싱 단계에서 **줄 끝 공백을 먼저 버린다**. 그래서 다시 열면 인라인 토큰의
 * 내용이 `[ ]` 가 되고, `markdown-it-task-lists` 는 `'[ ] '`(공백 포함)로 시작할 때만
 * 체크박스로 인정하므로 이 항목을 놓친다. 결국 평범한 글머리 항목의 **글자**가 되고,
 * 다음 저장 때 대괄호가 escape 되어 `- \[ \]` 로 굳는다 — 되돌릴 수 없다.
 * (운영 DB 실측 1건: `tasks.description`)
 *
 * 고치는 자리는 파싱 쪽이다. 직렬화 결과에 공백을 덧붙이는 식의 교정은 `markdown-it`
 * 이 그 공백을 이미 버린 뒤라 효과가 없음이 실측으로 확인됐다.
 *
 * 그래서 `markdown-it` 코어 규칙을 하나 얹어, 내용이 정확히 `[ ]`·`[x]`·`[X]` 인
 * 목록 항목을 **`markdown-it-task-lists` 와 똑같은 모양으로** 체크박스 토큰으로
 * 바꾼다. 플러그인이 우리보다 먼저 돌든 나중에 돌든 결과가 같도록(양쪽 다 상대가
 * 이미 처리한 항목은 건드리지 않는다) 변환을 통째로 수행해 등록 순서에 의존하지
 * 않는다.
 *
 * 사용자가 **글자로** 적은 `[ ]` 는 직렬화에서 `\[ \]` 로 escape 되므로 인라인 토큰의
 * 원문(`token.content`)이 달라 여기 걸리지 않는다 — 렌더된 DOM 만 보고 판단하면
 * 둘을 구분할 수 없어서, 굳이 토큰 단계에서 처리한다.
 */

const EMPTY_TASK_MARKERS = new Map<string, boolean>([
  ['[ ]', false],
  ['[x]', true],
  ['[X]', true],
]);

const CHECKBOX_HTML = {
  unchecked: '<input class="task-list-item-checkbox" disabled="" type="checkbox">',
  checked: '<input class="task-list-item-checkbox" checked="" disabled="" type="checkbox">',
} as const;

interface MarkdownItToken {
  type: string;
  content: string;
  level: number;
  children: MarkdownItToken[] | null;
  attrIndex: (name: string) => number;
  attrPush: (attr: [string, string]) => void;
  attrs: [string, string][] | null;
}

interface MarkdownItCoreState {
  tokens: MarkdownItToken[];
  Token: new (type: string, tag: string, nesting: number) => MarkdownItToken;
}

interface MarkdownItLike {
  core: {
    ruler: {
      after: (
        afterName: string,
        ruleName: string,
        rule: (state: MarkdownItCoreState) => void,
      ) => void;
    };
  };
}

/** `markdown-it-task-lists` 의 attrSet 과 동일 — 있으면 덮고 없으면 추가. */
function setAttr(token: MarkdownItToken | undefined, name: string, value: string): void {
  if (!token) return;
  const index = token.attrIndex(name);
  if (index < 0) token.attrPush([name, value]);
  else if (token.attrs) token.attrs[index] = [name, value];
}

/** 주어진 토큰을 감싸는 한 단계 바깥 토큰(목록 열기)의 인덱스. */
function parentTokenIndex(tokens: MarkdownItToken[], index: number): number {
  const target = tokens[index];
  if (!target) return -1;
  const targetLevel = target.level - 1;
  for (let i = index - 1; i >= 0; i -= 1) {
    if (tokens[i]?.level === targetLevel) return i;
  }
  return -1;
}

function convertEmptyTaskItems(state: MarkdownItCoreState): void {
  const { tokens } = state;
  for (let i = 2; i < tokens.length; i += 1) {
    const token = tokens[i];
    if (!token || token.type !== 'inline') continue;
    if (tokens[i - 1]?.type !== 'paragraph_open') continue;
    const itemToken = tokens[i - 2];
    if (itemToken?.type !== 'list_item_open') continue;

    const checked = EMPTY_TASK_MARKERS.get(token.content);
    if (checked === undefined) continue;

    const checkbox = new state.Token('html_inline', '', 0);
    checkbox.content = checked ? CHECKBOX_HTML.checked : CHECKBOX_HTML.unchecked;
    const children = [checkbox, ...(token.children ?? [])];
    // 마커 글자(`[ ]`)는 지운다 — 체크박스가 그 자리를 대신한다.
    const marker = children[1];
    if (marker) marker.content = '';
    token.children = children;
    token.content = '';

    setAttr(itemToken, 'class', 'task-list-item');
    const listIndex = parentTokenIndex(tokens, i - 2);
    if (listIndex >= 0) setAttr(tokens[listIndex], 'class', 'contains-task-list');
  }
}

/**
 * 기본 TaskItem 확장에 마크다운 파싱 보정을 얹은 것.
 *
 * `getMarkdownSpec` 은 `parse` 를 통째로 교체하므로 `tiptap-markdown` 기본 스펙의
 * `updateDOM`(체크박스 input → `data-checked` 속성 변환)을 함께 다시 제공해야 한다.
 */
export const AxMarkdownTaskItem = TaskItem.extend({
  addStorage() {
    const parent = this.parent?.() as Record<string, unknown> | undefined;
    return {
      ...parent,
      markdown: {
        serialize(
          state: { write: (text: string) => void; renderContent: (node: ProseMirrorNode) => void },
          node: ProseMirrorNode,
        ) {
          const check = (node.attrs as { checked?: boolean }).checked ? '[x]' : '[ ]';
          state.write(`${check} `);
          state.renderContent(node);
        },
        parse: {
          setup(markdownit: MarkdownItLike) {
            const guarded = markdownit as MarkdownItLike & { axEmptyTaskItemReady?: boolean };
            // setup 은 parse 호출마다 실행된다 — 규칙이 매번 쌓이지 않게 한 번만 건다.
            if (guarded.axEmptyTaskItemReady) return;
            guarded.axEmptyTaskItemReady = true;
            markdownit.core.ruler.after('inline', 'axEmptyTaskItem', convertEmptyTaskItems);
          },
          updateDOM(element: HTMLElement) {
            [...element.querySelectorAll('.task-list-item')].forEach((item) => {
              const input = item.querySelector('input');
              item.setAttribute('data-type', 'taskItem');
              if (input instanceof HTMLInputElement) {
                item.setAttribute('data-checked', String(input.checked));
                input.remove();
              }
            });
          },
        },
      },
    };
  },
});
